import log from '@/logger';
import * as XLSX from 'xlsx';

import getSearchRegExp from '@/utils/getSearchRegExp';
import stringToArrayBuffer from '@/utils/stringToArrayBuffer';

export const getPersonById = ({ persons }) => {
  return (id) => {
    const person = id ? persons[id] : null;

    return person && !person.archived ? person : null;
  };
};

export const managingPersons = (state) => {
  const currentProjectId = state.currentProject._id;
  return Object.values(state.persons).filter((person) => {
    return (
      !!person &&
      !person.archived &&
      person.userId !== '' &&
      (person.role === 'admin' || (person.projects && person.projects.find(({ _id }) => _id === currentProjectId)))
    );
  });
};

export const projectPersons = (state) => {
  const list = [];
  if (!state.currentProject.projectManagers) {
    return list;
  }
  let pIds = new Set();
  for (let c = 0; c < state.currentProject.projectManagers.length; c++) {
    let personId = state.currentProject.projectManagers[c].personId;
    if (personId) {
      pIds.add(personId);
    }
  }
  for (let c = 0; c < state.currentProject.projectSubcontractors.length; c++) {
    let personId = state.currentProject.projectSubcontractors[c].personId;
    if (personId) {
      pIds.add(personId);
    }
  }
  for (const pId in state.persons) {
    if (state.persons.hasOwnProperty(pId)) {
      let person = state.persons[pId];
      if (!person || person.archived || person.userId === '' || pIds.has(person._id)) {
        continue;
      }

      if (
        person.role === 'admin' ||
        (person.projects &&
          person.projects.find((p) => {
            return p._id === state.currentProject._id;
          }))
      ) {
        list.push(person);
      }
    }
  }
  for (let pId of pIds) {
    let person = state.persons[pId];

    if (!person) {
      continue;
    }
    list.push(person);
  }
  return list;
};

export const groupedPersons = (state, getters) => (filter) => {
  const persons = getters.persons(filter);
  const list = [];

  for (const companyId in persons) {
    const company = state.companies[companyId];
    if (company && !company.archived) {
      const c = Object.assign({}, company, { persons: persons[companyId] });
      list.push(c);
    }
  }
  return list;
};

const personFilters = {
  byArchived: ({ archived }) => !archived,

  byCompany: (companyId) => (person) => person.companyId === companyId,

  byText: (text) => {
    const regexp = getSearchRegExp(text);
    return ({ firstName, lastName, occupation, email, username }) => {
      return (
        regexp.test(firstName) ||
        regexp.test(lastName) ||
        regexp.test(email) ||
        regexp.test(username) ||
        (!Array.isArray(occupation) && regexp.test(occupation))
      );
    };
  },

  byCity: (city) => {
    const value = city.toLowerCase();
    return ({ city }) => city && !Array.isArray(city) && value === city.toLowerCase();
  },

  byOccupation: (occupation) => {
    const value = occupation.toLowerCase();
    return ({ occupation }) => occupation && !Array.isArray(occupation) && value === occupation.toLowerCase();
  },

  byProject: (projectId) => {
    return ({ projects, userId }) => {
      return projects && userId !== '' && projects.some(({ _id }) => _id === projectId);
    };
  },

  byCompanyName: (companies, companyName, managingCompany) => {
    return ({ companyId }) => {
      const company = companies[companyId];
      return (
        !!company &&
        ((companyName && company.companyName.toLowerCase() === companyName.toLowerCase()) ||
          (managingCompany && company.managingCompany))
      );
    };
  },
};

export const persons = (state) => (values) => {
  const result = {};
  if (!state.persons) {
    return result;
  }

  const filters = [personFilters.byArchived];
  if (values) {
    values.companyId && filters.push(personFilters.byCompany(values.companyId));
    values.projectId && filters.push(personFilters.byProject(values.projectId));
    if (values.managingCompany || values.companyName) {
      filters.push(personFilters.byCompanyName(state.companies, values.companyName, values.managingCompany));
    }
    values.city && filters.push(personFilters.byCity(values.city));
    values.occupation && filters.push(personFilters.byOccupation(values.occupation));
    values.text && filters.push(personFilters.byText(values.text));

    // Skip filtering by persons if used filters unrelated to persons.
    if (filters.length === 1 && (values.country || values.withMissingUeFile || values.withCodeOfConductStatus)) {
      return result;
    }
  }

  Object.values(state.persons).forEach((person) => {
    if (!filters.every((filter) => filter(person))) {
      return;
    }

    if (result[person.companyId]) {
      result[person.companyId].push(person);
    } else {
      result[person.companyId] = [person];
    }
  });

  Object.values(result).forEach((items) => {
    // Sorted in place, which means each `result[comapnyId]` array is modified directly.
    items.sort((a, b) => {
      const at = a.firstName.toLowerCase();
      const bt = b.firstName.toLowerCase();
      switch (true) {
        case at < bt:
          return -1;

        case at > bt:
          return 1;

        case a._id < b._id:
          return -1;
      }

      return 1;
    });
  });

  return result;
};

export const lastCommentReadDateAtLogin = (state) => {
  return state.lastCommentReadDateAtLogin;
};

export const loggedInPerson = (state) => {
  return state.loggedInPerson;
};

export const occupations = (state) => {
  const list = [];
  if (!state.persons || !state.companies) {
    return list;
  }

  Object.values(state.persons).forEach(({ archived, occupation }) => {
    if (!archived && occupation) {
      if (Array.isArray(occupation)) {
        list.push(...occupation.flat(5));
      } else {
        list.push(occupation);
      }
    }
  });

  Object.values(state.companies).forEach(({ archived, mainBusiness }) => {
    if (!archived && mainBusiness && mainBusiness.length) {
      list.push(...mainBusiness.flat(5));
    }
  });

  return [...new Set(list)].sort();
};

export const getPerson = ({ persons }) => {
  return (id) => {
    /** @todo This is... just ugh... Must rework this */
    return persons[id] || { firstName: 'Unknown: ' + id, _id: id };
  };
};

export const personByUsername = (state) => (value) => {
  const username = value ? value.toLowerCase().trim() : '';
  return Object.values(state.persons).find(
    (person) => !!person.username && person.username.toLowerCase().trim() === username
  );
};

export const getPersonByName = (state) => (name) => {
  name = name.toLowerCase();
  for (const id in state.persons) {
    if (state.persons.hasOwnProperty(id)) {
      const person = state.persons[id];
      if (person.firstName.toLowerCase() + ' ' + person.lastName.toLowerCase() === name) {
        return person;
      }
    }
  }

  log.warn('personGetters:getPersonByName.not found', [name]);
};

export const personName = (state) => (id) => {
  const person = state.persons[id];

  if (person) {
    return (person.firstName + ' ' + person.lastName).trim();
  }

  return '';
};

export const personCompanyName = (state) => (id) => {
  const person = state.persons[id];
  if (!person) {
    return '';
  }

  const fullName = [person.firstName, person.lastName].filter(Boolean).join(' ').trim();
  const company = person.companyId ? state.companies[person.companyId] : null;
  const companyName = company && company.companyName && company.companyName.trim();

  return companyName ? [fullName, `(${companyName})`].filter(Boolean).join(' ').trim() : fullName;
};

export const personCompanyOrPersonName = (state) => (id) => {
  const person = state.persons[id];

  if (person) {
    const company = state.companies[person.companyId];
    return (company ? company.companyName : person.firstName + ' ' + person.lastName).trim();
  }

  return '';
};

export const exportOrganizationToXlsx = (_state, getters) => (items) => {
  const aCharCode = 'A'.charCodeAt(0);
  const zCharCode = 'Z'.charCodeAt(0);
  const getColumnLetter = (columnNumber) => {
    // Simple formula to calculate column letter. Supports columns up to Z.
    return String.fromCharCode(Math.min(zCharCode, aCharCode + Math.max(0, columnNumber)));
  };

  const addColumns = (sheetData, columns, rowNumber) => {
    columns.forEach((value, index) => {
      const column =
        typeof value === 'object' && value != null
          ? { ...value }
          : { t: 's', v: typeof value === 'string' ? value : '' };
      if (!column.t) {
        column.t = 's';
      }
      sheetData[`${getColumnLetter(index)}${rowNumber}`] = column;
    });
  };

  const book = XLSX.utils.book_new();
  book.Props = {
    Title: 'Boon Organisations',
    Subject: 'Export',
    Author: getters.personName(getters.loggedInPerson._id),
    CreatedDate: new Date(),
  };

  let rowsCount = 0;
  const sheetData = {
    // Width of columns
    '!cols': [
      { wch: 30 },
      { wch: 15 },
      { wch: 15 },
      { wch: 15 },
      { wch: 15 },
      { wch: 25 },
      { wch: 15 },
      { wch: 8 },
      { wch: 15 },
      { wch: 15 },
      { wch: 15 },
    ],
  };
  const header = [
    { v: 'Company name', t: 'yyyy', s: { font: { bold: true } } },
    { v: 'First name', s: { font: { bold: true } } },
    { v: 'Last name', s: { font: { bold: true } } },
    { v: 'Phone', s: { font: { bold: true } } },
    { v: 'Occupation', s: { font: { bold: true } } },
    { v: 'E-mail', s: { font: { bold: true } } },
    { v: 'Street', s: { font: { bold: true } } },
    { v: 'Zip code', s: { font: { bold: true } } },
    { v: 'City', s: { font: { bold: true } } },
    { v: 'Next of kin', s: { font: { bold: true } } },
    { v: 'Next of kin phone', s: { font: { bold: true } } },
  ];
  addColumns(sheetData, header, ++rowsCount);

  const emptyCell = { v: '-', alignment: { horizontal: 'center', vertical: 'center' } };
  items.forEach(({ company, persons }) => {
    if (!company || company.archived) {
      return;
    }

    const companyRow = [
      company.companyName || emptyCell,
      '',
      '',
      company.phone || emptyCell,
      '',
      company.email || emptyCell,
      company.address.street || emptyCell,
      company.address.zipCode || emptyCell,
      company.address.city || emptyCell,
      '',
      '',
    ];

    addColumns(sheetData, companyRow, ++rowsCount);

    persons.forEach((person) => {
      const personRow = [
        company.companyName || emptyCell,
        person.firstName || emptyCell,
        person.lastName || emptyCell,
        person.phone || emptyCell,
        person.occupation || emptyCell,
        person.email || emptyCell,
        '',
        '',
        person.city || emptyCell,
        person.nextOfKin || emptyCell,
        person.nextOfKinPhone || emptyCell,
      ];
      addColumns(sheetData, personRow, ++rowsCount);
    });

    addColumns(sheetData, [''], ++rowsCount);
    addColumns(sheetData, [''], ++rowsCount);
  });

  const lastColumnLetter = getColumnLetter(sheetData['!cols'].length - 1);
  // Specify data range for XLSX util. Data outside this range will not be rendered.
  sheetData['!ref'] = `A1:${lastColumnLetter}${Math.max(1, rowsCount)}`;

  XLSX.utils.book_append_sheet(book, sheetData, 'Organization');

  const document = XLSX.write(book, { bookType: 'xlsx', type: 'binary' });

  return stringToArrayBuffer(document);
};



// WEBPACK FOOTER //
// ./src/store/person/personGetters.js